/**
 * Headless action skirmishes (Phase 59.6): many action bots per side over capture points on
 * one world, respawning in waves, tickets bled by holding fewer points — Ravenfield's shape —
 * with the numbers the owner asked for first: time to capture, kill rate, how often a point
 * changes hands, how long points are held, how matches end. A thin host over Match.
 */

import { shotTally, usageByTeam, type TeamUsage } from './actionUsage';
import { spreadCollector, type SpreadSummary } from './actionSpread';
import type { GameState, PlanetSizePreset, PlayRefusal, Vec3 } from '../types';
import type { ActionEvent, SimPresenter, Match } from '../match/match';
import { createMatchState, matchFromState, type MatchSetup } from '../match/setup';
import { createActionBot, type ActionKnowledge } from './actionBot';
import { actionParamsDifferByTeam, actionParamsFor } from './botParams';
import { withArchetypes } from './botArchetypes';
import { coverLensCollector, type CoverLensSummary } from './actionCoverLens';
import { duelDigest, engagementCollector, brainSpecs, brainOptions, type BrainSpec, type EngagementSummary } from './actionDuel';
import type { JobWeights, ActionRole } from './actionJobs';
import type { PositionWeights } from './actionPositions';

/** §14 B4: a brain's weights — the jobs, the fight spots, the roles (any may be absent = defaults). */
export interface BrainWeights { jobs?: JobWeights; positions?: PositionWeights; roles?: Record<ActionRole, JobWeights> }
import { isGameOver, checkWinCondition } from '../core/gameState';
import { PHYS_DT } from '../constants';
import { createCommander, hookAnswer, orderTo, type CommanderRefusal, type CommanderStyle, type OrderStats, type TacticStats, type DropCalls, type OrderClaim, type SearchRecord, type Squad } from './squadOrders';
import { seatActor } from '../match/input';
import { formationError } from '../core/formation';
import { isTactic } from '../core/tactics';
import { facingSpread } from '../core/holding';
import { personSetOf } from '../core/scaleProfile';
import { countOf, defaultOn } from '../core/defaultOn';

export interface SkirmishSetup extends MatchSetup {
  /** A harness's look inside (the every-switch check): each tick's events, and the finished match (its input log). Not in the report. */
  inspect?: { events?: (events: readonly ActionEvent[]) => void; done?: (match: Match) => void };
  /** Seconds before the mode's time rule decides (default 300). */
  timeLimit?: number;
  /** Record the Match's per-tick checksum every this many ticks into the report's tickTrace. */
  traceTicks?: number;
  /** Two teams trade landing spots (unit i of team 0 ↔ unit i of team 1): a mirror played both ways cancels a map's seat advantage. */
  swapSpawns?: boolean;
  pointCount?: number;
  tickets?: number;
  /** Layer 1: a commander style per team ('none' = the uncommanded bots, the baseline). */
  commanders?: CommanderStyle[];
  /** Units per squad under a commander (default 3). */
  squadSize?: number;
  /** 66.3: every squad moves in this formation (also a MatchSetup field; here so the CLI and the report can name it). */
  formationShape?: 'column' | 'wedge' | 'line';
  /** 66.4: the commanders issue group tactics (bound, flank) as tasks over their orders. Default off. */
  tactics?: boolean;
  /** 68.4: with reserves on, the commanders hold the fallen back and call them in a squad at a time (the table's `reserves` order). Default off. */
  callReserves?: boolean;
  /** 68.22 step 4: the commanders form plays (support and assault) where their squads would converge on a contested point. On by default (decision 159, the owner: "Yes plays on by default for commanders"); false turns them off. */
  commanderPlays?: boolean | readonly boolean[];   // (per team, as the Lab's per-side dial: Oct 13, plays against no plays)
  /** 66.5: holding squads take sectors of fire (each member faces its own way). Default off. */
  holding?: boolean;
  /** 66.7 C1: measure cover (ai/actionCoverLens.ts) — reported only with it on, so the golden's reports keep their shape. */
  coverLens?: boolean;
  /** Seats under threat: a gun's crew runs for it mid-fight (a short run, no enemy close). Default off. */
  seatsUnderThreat?: boolean;
  /** X22: exits on, and at `atSeconds` the team's seat orders every one of its squads to withdraw (a task through apply). */
  withdraw?: { team: number; atSeconds: number };
  /** X19: each team's reserve — its respawn pool, arriving at its arrival region (also a MatchSetup field). */
  reserves?: number[];
  /** Seconds between a commander's re-plans (default REPLAN_SECONDS). */
  replanSeconds?: number;
  /** WORKBOARD §13: per team, its people's knowledge feeds (default none). */
  knowledgeByTeam?: Array<ActionKnowledge | undefined>;
  /** §14 B1: the bots engage from scored spots — for everyone, or per team. */
  positions?: boolean | boolean[];
  /** §14: which bot brain, for everyone or per team (see actionDuel.BrainSpec). Wins over `positions`. */
  brain?: BrainSpec | BrainSpec[];
  /** 64.9 (decision 115): ticks from a bot's decision to its plan applying (default ACTION_DELIBERATION_DELAY). */
  delay?: number;
  /** §14 B4: per team, weights for its brain (an evolved candidate; absent = the brain's defaults). */
  weightsByTeam?: Array<BrainWeights | undefined>;
  /** D3: per team, whether its bots walk far destinations along routes (default off). */
  navigate?: boolean[];
  /** D3: route cost of being seen (default the bot's; 0 = geometry only). */
  navThreatWeight?: number;
}

export interface SkirmishReport {
  /** The hidden archetypes each team played (MatchSetup.archetypes on; absent off). */
  archetypes?: string[];
  /** 66.3 (with formationShape): the squads' mean slot error in person lengths. */
  formation?: { shape: 'column' | 'wedge' | 'line'; meanSlotError: number | null };
  /** 66.4 (with tactics): squad-seconds under a tactic, and each commander's ledger. */
  tactics?: { squadSeconds: number; byTeam: Array<TacticStats | undefined> };
  /** 66.5 (with holding): the mean pairwise facing angle of holding squads' members, degrees. */
  holding?: { facingSpread: number | null; samples: number };
  /** 66.7 C1 (with coverLens): how often bots are open to an enemy, moving and still, and how they die. */
  cover?: CoverLensSummary;
  /** X22 (with withdraw): units that got out, by team, and when the first did. */
  extraction?: { extracted: number; byTeam: Record<number, number>; firstOut: number | null };
  /** X19 (with reserves): arrivals by team and the reserve each side has left. */
  reserves?: { arrivals: number; byTeam: Record<number, { arrived: number; left: number }> };
  seed: string;
  winnerTeam: number | null;
  reason: string | null;
  time: number;
  planetRadius: number;
  /** Seconds to the first capture, or null. */
  firstCapture: number | null;
  captures: number;
  neutralisations: number;
  kills: number;
  respawns: number;
  shots: number;
  hits: number;
  /** Per point: share of the match each team owned it (team → 0–1), and how many times it flipped. */
  points: Array<{ held: Record<number, number>; flips: number }>;
  ticketsLeft: Record<number, number>;
  /** Layer 1: per team, what its commander ordered and what came of it (issued 0 under 'none'). */
  orders: Array<OrderStats & { style: CommanderStyle; meanComply: number }>;
  /**
   * 69.72 step 5 (DECISIONS 304: a refusal is shown to its giver): what the Match refused the commanders — per team, a count
   * by planner and code (`objStrike/claimed`) — and the plays' own re-tasks, by step and code (`ride/claimed`). Only when any.
   */
  refusals?: { commanders: Array<Record<string, number>>; plays: Record<string, number> };
  /** 69.73 step 2b (with intelRecord): each team's commander's search log (ai/squadOrders.ts SearchRecord). */
  searches?: SearchRecord[][];
  checksum: string;
  /** With traceTicks: the per-tick checksum every traceTicks ticks. */
  tickTrace?: number[];
  /** The same digest the page reports for an action match (actionDuel.duelDigest) — the smoke compares by it. */
  stateChecksum: string;
  /** §14: the kills' range, height and cover. */
  engagements: EngagementSummary;
  /** §14 B2: share of living bot-ticks per job, per team (utility brain only; empty otherwise). */
  jobShare: Record<number, Record<string, number>>;
  /** Harness upkeep (121): per team, the stances, reloads, weapons and formation slots the bots used (ai/actionUsage.ts). */
  usage: Record<number, TeamUsage>;
  /** 66.1: how the teams stood and how often their enemies were caught in crossfire (ai/actionSpread.ts). */
  spread: SpreadSummary;
}

/** 68.22: does team `t`'s commander form plays — the setup's one value, or its side's (on unless false). */
export function sidePlays(v: boolean | readonly boolean[] | undefined, t: number): boolean {
  return Array.isArray(v) ? v[t] !== false : v !== false;
}

/** The setup a skirmish starts from: one megaplanet (teams land ~90–130 u apart), the action clock, capture points with tickets (default POINTS_DEFAULT_TICKETS). */
export function skirmishMatchSetup(setup: SkirmishSetup): MatchSetup & Pick<SkirmishSetup, 'brain' | 'commanders' | 'tactics' | 'holding' | 'withdraw' | 'squadSize' | 'replanSeconds' | 'callReserves'> {   // reserves is a MatchSetup field already
  const { timeLimit, pointCount, tickets, swapSpawns: _swap, positions: _pos, brain: _brain, weightsByTeam: _w, traceTicks: _trace, delay: _delay, formationShape: _form, tactics: _tac, callReserves: _cr, holding: _hold, seatsUnderThreat: _sut, withdraw: _wd, ...rest } = setup;
  return {
    planets: 1, planetSize: 'megaplanet' as PlanetSizePreset, hazardDensity: 'none', ...rest,
    // R11 step 2: squads live in the state (the commanders' orders arrive as inputs) — since 68.2b's follow-up in every
    // skirmish, threes by default: the player's order menu (F) commands the squad of the unit you hold, wherever you play
    squadSize: rest.squadSize ?? 3,
    ...(setup.formationShape ? { formation: { shape: setup.formationShape } } : {}),
    ...(setup.withdraw ? { extraction: true, squadSize: rest.squadSize ?? 3 } : {}),
    // the skirmish-only switches ride on the returned object too: the page's game host and its in-page auto-resolve read
    // them from this setup (a dropped `tactics` made the page run without tactics while Node ran with them — the
    // action/tactics smoke diff, Oct 11; the same for holding and withdraw)
    ...(setup.tactics ? { tactics: true } : {}),
    ...(setup.callReserves ? { callReserves: true } : {}),   // 68.4: the page's commanders call the reserve as Node's do
    ...(setup.brain ? { brain: setup.brain } : {}),   // 67.24: the brain too (the Lab's skirmish lost its Bot brain row here; the page's own URL path re-added it)
    ...(setup.holding ? { holding: true } : {}),
    ...(setup.withdraw ? { withdraw: setup.withdraw } : {}),
    ...(setup.reserves ? { reserves: setup.reserves } : {}),
    clock: 'action', actionTimeLimit: timeLimit ?? 300,
    modeOverrides: { ...(rest.modeOverrides ?? {}), objective: 'points', respawn: true, ...(tickets !== undefined ? { respawnTickets: tickets } : {}), ...(pointCount ? { pointCount } : {}) },
  };
}

function digest(state: GameState): string {
  let h = 2166136261;
  const mix = (x: number) => { h = Math.imul(h ^ (Math.round(x * 1e6) | 0), 16777619) >>> 0; };
  for (const p of state.players) { mix(p.surfaceDir.x); mix(p.surfaceDir.y); mix(p.health); mix(p.kills); }
  for (const s of state.modeState.points?.sites ?? []) { mix(s.owner ?? -1); mix(s.progress); }
  for (const t of Object.values(state.modeState.respawn?.tickets ?? {})) mix(t);
  return h.toString(16);
}

/** 69.72 step 5: the report's refusal counts (absent when nothing was refused). */
function refusalCounts(commanders: ReadonlyArray<{ refusals: readonly CommanderRefusal[] }>, plays: readonly PlayRefusal[]): Pick<SkirmishReport, 'refusals'> {
  const count = <T>(xs: readonly T[], key: (x: T) => string) => { const out: Record<string, number> = {}; for (const x of xs) out[key(x)] = (out[key(x)] ?? 0) + 1; return out; };
  if (!plays.length && commanders.every(c => !c.refusals.length)) return {};
  return { refusals: { commanders: commanders.map(c => count(c.refusals, r => `${r.planner}/${r.code}`)), plays: count(plays, r => `${r.step}/${r.code}`) } };
}

export function runSkirmish(setup: SkirmishSetup): SkirmishReport {
  const { state: built } = createMatchState(skirmishMatchSetup(setup));
  const state = setup.swapSpawns ? swapTeamSpawns(built) : built;
  const match = matchFromState({ ...state, phase: 'aiming' });
  if (setup.traceTicks) { match.tickTrace = []; match.traceEvery = setup.traceTicks; }
  setup.onMatch?.(match);
  const teams = setup.teams ?? 2;
  // R11 step 2: a commander's orders enter the Match as the team seat's `order` inputs (base tier); the bots read state.squads
  const issueFor = (t: number) => (squad: Squad, order: Parameters<NonNullable<Parameters<typeof createCommander>[5]>>[1], tier: 'base' | 'task', expires?: number, units?: number[], claim?: OrderClaim) => {
    const to = orderTo(match.state, squad, units);   // (69.72 step 5's follow-up: a units task needs no squad; a squad by its first living member in one; 69.87: a Match squad by its id)
    if ('refused' in to) return to;
    return hookAnswer(match.apply(match.stamp(seatActor(t), { kind: 'order', team: t, to, tier, order, by: 'commander', ...(expires !== undefined ? { expires } : {}), ...claim })));   // (69.72 step 5: what it claims the squad for, DECISIONS 304; the refusal's code to the commander)
  };
  const playFor = (t: number) => (squads: Squad[], objective: number | { planetIndex: number; surfaceDir: Vec3 }, opts?: { support?: Squad[]; here?: true; flank?: true; mounted?: true }) => {   // 68.22 step 4; 66.15: a place, the support named
    const idOf = (q: Squad) => { const to = orderTo(match.state, q); return 'squad' in to ? to.squad : undefined; };   // (69.72 step 5's follow-up: by its first living member in a squad; 69.87: a Match squad by its id)
    const ids = [...new Set(squads.map(idOf).filter((x): x is number => x !== undefined))];
    const support = opts?.support?.map(idOf).filter((x): x is number => x !== undefined);
    return hookAnswer(match.apply(match.stamp(seatActor(t), { kind: 'coordinate', team: t, squads: ids, objective: typeof objective === 'number' ? { pointId: objective } : objective, by: 'commander', ...(support?.length ? { support } : {}), ...(opts?.here ? { here: true as const } : {}), ...(opts?.flank ? { flank: true as const } : {}), ...(opts?.mounted ? { mounted: true as const } : {}) })));
  };
  const reservesFor = (t: number) => (call: 'hold' | 'call') => { match.apply(match.stamp(seatActor(t), { kind: 'reserves', team: t, call })); };   // 68.4
  const airstrikeFor = (t: number) => (at: { planetIndex: number; surfaceDir: Vec3 }) => { match.apply(match.stamp(seatActor(t), { kind: 'airstrike', team: t, at })); };   // 68.39
  // 69.3b step 3: the side's drop calls (when it holds any) and its order on where its waves land — the commander's (by: 'commander')
  const dropsFor = (t: number): DropCalls => ({
    ...(countOf(match.state.config, 'helicopters') ? { heli: (at, order) => { match.apply(match.stamp(seatActor(t), { kind: 'dropship', team: t, at, payload: 'troops', order, by: 'commander', craft: 'heli' })); } } : {}),   // 69.59 step 1
    ...(match.state.dropships ? { dropship: (at, order, hot) => { match.apply(match.stamp(seatActor(t), { kind: 'dropship', team: t, at, payload: 'troops', order, by: 'commander', ...(hot ? { hot: true as const } : {}) })); } } : {}),   // (69.52 step 4: hot)
    ...(match.state.pods ? { pods: (at, order) => { match.apply(match.stamp(seatActor(t), { kind: 'podDrop', team: t, at, order, by: 'commander' })); } } : {}),
    ...(defaultOn(match.state.config, 'shipPickup') || match.state.config.storyCarry ? { pickup: (squad, at, priority, craft) => { match.apply(match.stamp(seatActor(t), { kind: 'pickup', team: t, squad, at, by: 'commander', ...(priority ? { priority } : {}), ...(craft ? { craft } : {}) })); } } : {}),   // 69.18
    ...(countOf(match.state.config, 'gunships') ? { gunship: at => { match.apply(match.stamp(seatActor(t), { kind: 'dropship', team: t, at, payload: 'gunship', by: 'commander' })); } } : {}),   // 63.13
    ...(match.state.config.hotRescue ? { rescue: (squad, at, threat, cause) => { match.apply(match.stamp(seatActor(t), { kind: 'rescue', team: t, squad, at, threat, by: 'commander', ...(cause ? { cause } : {}) })); } } : {}),   // 69.62
    ...(match.state.supplyDrones ? { supplyDrop: (at, load) => { match.apply(match.stamp(seatActor(t), { kind: 'supplyDrop', team: t, at, load, by: 'commander' })); } } : {}),   // 69.32 step 2
    spawnAt: at => { match.apply(match.stamp(seatActor(t), { kind: 'spawnAt', team: t, at })); },
  });
  const commanders = Array.from({ length: teams }, (_, t) => createCommander(match.state, t, setup.commanders?.[t] ?? 'none', setup.squadSize ?? 3, setup.replanSeconds, issueFor(t), !!setup.tactics, setup.callReserves ? reservesFor(t) : undefined, sidePlays(setup.commanderPlays, t) ? playFor(t) : undefined, match.state.airstrikes ? airstrikeFor(t) : undefined, dropsFor(t), match.state.config.efforts ? efforts => { match.apply(match.stamp(seatActor(t), { kind: 'efforts', team: t, efforts, by: 'commander' })); } : undefined));   // (§0.11 step D)
  // 66.3 / 66.4 measures (only reported with the flags on, so the golden's reports keep their shape)
  let slotSamples = 0, slotErrorSum = 0, tacticTicks = 0, holdSamples = 0, holdSpreadSum = 0;
  // the setup's bot params, with the teams' hidden archetypes under them when it has them (setup drew them; botArchetypes.ts)
  const botParams = withArchetypes(setup.botParams, match.state.botArchetypes);
  const botFor = (spec: BrainSpec, w?: BrainWeights, team = 0) => createActionBot(setup.seed, {
    ...(botParams ? { params: actionParamsFor(botParams, team) } : {}),   // 62.48
    knowledgeFor: t => setup.knowledgeByTeam?.[t],
    ...(setup.sharedSight ? { shareSight: true } : {}),
    ...(setup.holding ? { holdingGround: true } : {}),
    ...(setup.seatsUnderThreat ? { seatsUnderThreat: true } : {}),
    navigate: unit => !!setup.navigate?.[unit.teamIndex],
    navThreatWeight: setup.navThreatWeight,
    ...brainOptions(spec),
    ...(setup.delay !== undefined ? { delay: setup.delay } : {}),
    ...(w?.jobs ? { jobWeights: w.jobs } : {}),
    ...(w?.positions ? { positions: w.positions } : {}),
    ...(w?.roles ? { roleWeights: w.roles } : {}),
  });
  const specs = brainSpecs(setup);
  const specList = Array.isArray(specs) ? specs : [specs];
  // One driver per team when brains or weights differ by team (a head-to-head); one for all otherwise
  const paramsByTeam = actionParamsDifferByTeam(botParams);
  const perTeam = specList.length > 1 || (setup.weightsByTeam?.length ?? 0) > 1 || paramsByTeam;
  const drivers = perTeam
    ? Array.from({ length: Math.max(specList.length, setup.weightsByTeam?.length ?? 1, paramsByTeam ? teams : 1) }, (_, t) => botFor(specList[t % specList.length], setup.weightsByTeam?.[t], t))
    : [botFor(specList[0], setup.weightsByTeam?.[0])];
  match.actionDriver = drivers.length > 1 ? (m, u) => drivers[u.teamIndex % drivers.length].drive(m, u) : drivers[0].drive;
  const engagements = engagementCollector(match);
  const coverLens = setup.coverLens ? coverLensCollector() : null;
  const tally = shotTally();
  const spread = spreadCollector();
  const sites0 = match.state.modeState.points?.sites ?? [];
  const held = sites0.map(() => new Map<number, number>());
  const flips = sites0.map(() => 0);
  let firstCapture: number | null = null, captures = 0, neutralisations = 0, kills = 0, respawns = 0, shots = 0, hits = 0, over = false;
  const onEvents = (events: ActionEvent[], gameOver: boolean) => {
    const t = match.actionTick * PHYS_DT;
    for (const e of events) {
      if (e.kind === 'captured') { captures++; flips[e.pointId]++; if (firstCapture === null) firstCapture = t; }
      else if (e.kind === 'neutralised') neutralisations++;
      else if (e.kind === 'respawned') respawns++;
      else if (e.kind === 'fired') { shots++; tally.fired(match.state, e.unitId); }
      else if (e.kind === 'hitUnit') { hits++; spread.hit(match.state, e.bullet.ownerId, e.targetId, match.actionTick); if (e.killed) { kills++; engagements.add(e.bullet.ownerId, e.targetId); coverLens?.death(match.state, e.targetId); } }
      // S7 / 64.11: kills by a rocket's burst or a seat's shell count too (a match without them reports as before)
      else if (e.kind === 'blast') { for (const v of e.kills) { kills++; engagements.add(e.ownerId, v); coverLens?.death(match.state, v); } }
      else if (e.kind === 'shellImpact') { for (const v of e.resolution.kills) { kills++; engagements.add(e.shell.ownerId, v); coverLens?.death(match.state, v); } }
      else if (e.kind === 'struck' && e.killerId !== undefined) { kills++; engagements.add(e.killerId, e.unitId); coverLens?.death(match.state, e.unitId); }   // 63.19: run down by a driven hull
    }
    setup.inspect?.events?.(events);
    if (gameOver) over = true;
  };
  const noop = () => {};
  const p: SimPresenter = {
    holdFlightEnd: () => false, flightEndReached: noop, launched: noop, probeScan: noop,
    geyserDeflect: noop, impacted: noop, lost: noop, flightOver: noop, airborne: noop, actionEvents: onEvents,
  };
  const cap = Math.round(((setup.timeLimit ?? 300) + 5) / PHYS_DT);
  // the commanders re-plan before a tick, the hold tally counts after it (64.1: through the Match's runTicks)
  match.runTicks(cap - match.actionTick, {
    ...p,
    beforeTick: () => {
      for (const c of commanders) c.step(match.state, match.actionTick, PHYS_DT);
      // X22: the scripted withdrawal — the team seat's task on each of its squads, once
      if (setup.withdraw && match.actionTick === Math.round(setup.withdraw.atSeconds / PHYS_DT)) {
        const t = setup.withdraw.team;
        for (const sq of (match.state.squads ?? []).filter(q => q.team === t)) match.apply(match.stamp(seatActor(t), { kind: 'order', team: t, to: { squad: sq.id }, tier: 'task', order: { kind: 'withdraw' }, by: 'setup' }));   // (69.72 step 5, DECISIONS 304: the scripted withdrawal, tagged)
      }
    },
    afterTick: () => {
      if (match.actionTick % 30 === 0) spread.sample(match.state);   // 66.1: a look at how the teams stand, four times a second
      if (coverLens && match.actionTick % 15 === 0) coverLens.sample(match.state, match.actionTick);   // 66.7 C1: eight times a second
      const sites = match.state.modeState.points?.sites ?? [];
      sites.forEach((s, i) => { if (s.owner !== null) held[i].set(s.owner, (held[i].get(s.owner) ?? 0) + 1); });
      if ((setup.formationShape || setup.tactics || setup.holding) && match.actionTick % 24 === 0) {
        if (setup.formationShape) { const e = formationError(match.state, personSetOf(match.state).unitRadius * 2); if (e !== null) { slotSamples++; slotErrorSum += e; } }
        if (setup.tactics) tacticTicks += (match.state.squads ?? []).filter(sq => sq.tasks.some(t => isTactic(t.order) && (t.expires === undefined || match.actionTick < t.expires))).length;
        if (setup.holding) for (const sq of match.state.squads ?? []) if (sq.base?.kind === 'hold') { holdSamples++; holdSpreadSum += facingSpread(match.state, sq); }
      }
    },
  }, () => over);
  const s = match.state;
  const ticks = Math.max(1, match.actionTick);
  const win = isGameOver(s) ? checkWinCondition(s) : null;
  setup.inspect?.done?.(match);
  return {
    seed: setup.seed,
    ...(match.state.botArchetypes ? { archetypes: match.state.botArchetypes } : {}),
    winnerTeam: win?.winningTeamIndex ?? null,
    reason: win?.reason ?? null,
    time: match.actionTick * PHYS_DT,
    planetRadius: s.planets[s.modeState.points?.sites[0]?.planetIndex ?? 0]?.radius ?? 0,
    firstCapture, captures, neutralisations, kills, respawns, shots, hits,
    points: held.map((m, i) => ({ held: Object.fromEntries([...m].map(([t, n]) => [t, n / ticks])), flips: flips[i] })),
    ticketsLeft: { ...(s.modeState.respawn?.tickets ?? {}) },
    orders: commanders.map(c => { const st = c.stats(); return { ...st, style: c.style, meanComply: st.meanComplySeconds * PHYS_DT }; }),
    ...refusalCounts(commanders, match.playRefusals),
    ...(s.config.intelRecord ? { searches: commanders.map(c => [...c.searches]) } : {}),   // 69.73 step 2b
    // 66.3: how well the squads kept their shape (mean slot error in person lengths over the match); 66.4: tactic use
    ...(setup.formationShape ? { formation: { shape: setup.formationShape, meanSlotError: slotSamples ? slotErrorSum / slotSamples / (personSetOf(s).unitRadius * 2) : null } } : {}),
    ...(setup.tactics ? { tactics: { squadSeconds: tacticTicks * 24 * PHYS_DT, byTeam: commanders.map(c => c.stats().tactics) } } : {}),
    // 66.5: how widely a holding squad's members face (mean pairwise heading angle, degrees, over holding squads sampled)
    ...(coverLens ? { cover: coverLens.summary() } : {}),
    ...(setup.holding ? { holding: { facingSpread: holdSamples ? (holdSpreadSum / holdSamples) * 180 / Math.PI : null, samples: holdSamples } } : {}),
    // X22: who got out
    ...(setup.withdraw ? { extraction: { extracted: (s.extractions ?? []).length, byTeam: Object.fromEntries([...new Set(s.players.map(p => p.teamIndex))].sort().map(t => [t, (s.extractions ?? []).filter(e => e.teamIndex === t).length])), firstOut: s.extractions?.[0] ? s.extractions[0].tick * PHYS_DT : null } } : {}),
    // X19: the reserves that arrived, by team, and what each side has left
    ...(setup.reserves ? { reserves: { arrivals: (s.reserveArrivals ?? []).length, byTeam: Object.fromEntries([...new Set(s.players.map(p => p.teamIndex))].sort().map(t => [t, { arrived: (s.reserveArrivals ?? []).filter(a => a.teamIndex === t).length, left: s.modeState.respawn?.tickets[t] ?? 0 }])) } } : {}),
    checksum: digest(s),
    ...(match.tickTrace ? { tickTrace: match.tickTrace } : {}),
    stateChecksum: duelDigest(s),
    engagements: engagements.summary(),
    jobShare: (() => {
      const out: Record<number, Record<string, number>> = {};
      for (const d of drivers) for (const [id, st] of d.stats) {
        if (!st.jobs) continue;
        const t = s.players.find(p => p.id === id)?.teamIndex ?? 0;
        const row = out[t] ?? (out[t] = {});
        for (const [j, n] of Object.entries(st.jobs)) row[j] = (row[j] ?? 0) + (n ?? 0);
      }
      for (const row of Object.values(out)) { const tot = Object.values(row).reduce((a, b) => a + b, 0) || 1; for (const j of Object.keys(row)) row[j] = row[j] / tot; }
      return out;
    })(),
    spread: spread.summary(),
    usage: usageByTeam(drivers.flatMap(d => [...d.stats]), s, tally),
  };
}

/** Team 0 and team 1 trade landing spots, unit for unit (sorted by id); everything else stays. */
export function swapTeamSpawns(state: GameState): GameState {
  const a = state.players.filter(p => p.teamIndex === 0).sort((x, y) => x.id - y.id);
  const b = state.players.filter(p => p.teamIndex === 1).sort((x, y) => x.id - y.id);
  const spot = new Map<number, { planetIndex: number; surfaceDir: typeof a[number]['surfaceDir'] }>();
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    spot.set(a[i].id, { planetIndex: b[i].planetIndex, surfaceDir: b[i].surfaceDir });
    spot.set(b[i].id, { planetIndex: a[i].planetIndex, surfaceDir: a[i].surfaceDir });
  }
  return { ...state, players: state.players.map(p => spot.has(p.id) ? { ...p, ...spot.get(p.id)! } : p) };
}
